import { createContext, useContext, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useLocation } from 'react-router'
import { ApiError, get, type User } from './api'
import { useLive, type Conn } from './live'
import type { UserView } from './api'

interface Session {
  user: User | null
  loading: boolean
  refresh: () => Promise<unknown>
  conn: Conn
  view: UserView | null
  /** when live data last arrived (ms) */
  at: number
}

const Ctx = createContext<Session | null>(null)

/**
 * Pages that need no live data: signing an app in (/oauth/authorize, /device) and the
 * sign-in pages. A tab left open there after "Allow" must not hold one of the user's
 * streams (SSE-TABS: the OBS plugin's sign-in tab did).
 */
export function wantsLive(pathname: string): boolean {
  return !/^\/(oauth\/|device(\/|$)|login(\/|$)|register(\/|$))/.test(pathname)
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient()
  const me = useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        return await get<User>('/api/me')
      } catch (e) {
        if (e instanceof ApiError && (e.status === 401 || e.status === 403)) return null
        throw e
      }
    },
    staleTime: 60_000,
    // the plan's state changes without the user doing anything (a gift ends, an admin freezes):
    // read again once a minute while there is a plan, until the center sends me.changed (BILL-B3)
    refetchInterval: (q) => (q.state.data?.plan ? 60_000 : false),
  })
  const user = me.data ?? null
  const { pathname } = useLocation()
  const live = useLive(!!user && wantsLive(pathname), () => void qc.invalidateQueries({ queryKey: ['me'] }))
  return (
    <Ctx.Provider
      value={{
        user,
        loading: me.isLoading,
        refresh: () => qc.invalidateQueries({ queryKey: ['me'] }),
        conn: live.conn,
        view: live.view,
        at: live.at,
      }}
    >
      {children}
    </Ctx.Provider>
  )
}

export function useSession(): Session {
  const s = useContext(Ctx)
  if (!s) throw new Error('useSession outside SessionProvider')
  return s
}
